import { AlertCircle, CheckCircle2, LifeBuoy } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * The frame around every step of signing in.
 *
 * Signing in, the second factor and a forced password change used to be three
 * hand-rolled pages with different landmarks, logo sizes and spacing, so the
 * second step looked like a different product from the first. One layout
 * keeps them visibly one journey.
 */
export function AuthLayout({
  icon,
  title,
  subtitle,
  children,
  footer,
}: {
  icon?: ReactNode;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <main className="bg-sheen flex min-h-full items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex size-11 items-center justify-center rounded-card bg-accent text-white shadow-raised">
            {icon ?? <LifeBuoy aria-hidden className="size-6" />}
          </div>
          <h1 className="text-lg font-semibold tracking-tight text-ink">{title}</h1>
          {subtitle && <p className="mt-1 text-body text-ink-muted">{subtitle}</p>}
        </div>

        <div className="rounded-card border border-line bg-surface p-6 shadow-raised">{children}</div>

        {footer && <div className="mt-6 space-y-2 text-center text-mini text-ink-muted">{footer}</div>}
      </div>
    </main>
  );
}

/**
 * A message at the top of an auth card.
 *
 * Only for what has no field to sit beside — wrong credentials, a locked
 * account, a timed-out attempt. A problem with one field goes under that
 * field, once; saying it in both places made an ordinary typo look like two
 * failures.
 */
export function AuthNotice({
  tone,
  children,
  reference,
}: {
  tone: 'critical' | 'positive' | 'caution';
  children: ReactNode;
  /** A request id, worth showing only when the server itself failed. */
  reference?: string | undefined;
}) {
  const Icon = tone === 'positive' ? CheckCircle2 : AlertCircle;
  return (
    <div
      // A failure interrupts; a confirmation ("password changed") waits its turn.
      role={tone === 'critical' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2 rounded-control border px-3 py-2',
        tone === 'critical' && 'border-critical-line bg-critical-soft text-critical-ink',
        tone === 'positive' && 'border-positive-line bg-positive-soft text-positive-ink',
        tone === 'caution' && 'border-caution-line bg-caution-soft text-caution-ink',
      )}
    >
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0 text-body">
        {children}
        {reference && <p className="mt-0.5 font-mono text-mini opacity-80">Reference: {reference}</p>}
      </div>
    </div>
  );
}

/** "or use your email" between single sign-on and the password form. */
export function AuthDivider({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span aria-hidden className="h-px flex-1 bg-line" />
      <span className="text-mini text-ink-muted">{children}</span>
      <span aria-hidden className="h-px flex-1 bg-line" />
    </div>
  );
}
